import { HttpClient } from "@angular/common/http";
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import {
  Observable,
  Subject,
  Subscriber,
  Subscription,
  forkJoin,
  of,
} from "rxjs";
import { switchMapTo, take, takeUntil } from "rxjs/operators";
import { platform } from "../../../../platform/platform";
import { ApplicationError } from "../../../_errors/application-error";
import { Inspection } from "../../../_objects/inspection";
import {
  AudioWrapper,
  BookMarkInfo,
  BookMarkInfoWrapper,
} from "../../../_objects/inspection-image";
import { InspectionReport } from "../../../_objects/inspection-report";
import { TenantDataFile } from "../../../_objects/tenant-data";
import { FILE_SYSTEM, IPC_CHANNEL } from "../../../_offline/electron.constants";
import { IplexGConnectModeService } from "../../../_offline/services/iplex-g-connect-mode.service";
import { IplexGSyncService } from "../../../_offline/services/iplex-g-sync.service";
import { UtilsService } from "../../../_services/_utils.service";
import { CommonSnackBarService } from "../../../_services/common-snack-bar.service";
import { FileSystemAccessService } from "../../../_services/file-system-access.service";
import { ImageUploadOfflineService } from "../../../_services/image-upload-offline.service";
import { ImageUploadService } from "../../../_services/image-upload.service";
import { LangUpdateService } from "../../../_services/lang-update.service";
import { NetworkStatusService } from "../../../_services/network-status.service";
import { SpinnerService } from "../../../_services/spinner.service";
import {
  BROWSER_TYPE,
  IMAGE_FILETYPE,
  IMAGE_FILETYPE_MAP,
  PLATFORM,
  UPLOAD_RESULT_CODE,
} from "../../../app.constants";
import { ElectronService } from "../../../electron.service";
import { UploadProgressDialogComponent } from "../upload-progress-dialog/upload-progress-dialog.component";
import { UploadProgressDialogService } from "../upload-progress-dialog/upload-progress-dialog.service";
import {
  DeviceFile,
  ImportIplexGImageWrapper,
} from "./save-iplex-g-images/save-iplex-g-images.component";
import { InspectionFileValidator } from "../../../_services/image-upload/inspection-file.validator";
import { FileManagerComponent, FileManagerConfig } from "../../../common-dialog/file-manager/file-manager.component";
import { OperationMode, SelectionMode } from "../../../common-dialog/file-manager/types";
import { getImageUploadPath } from "../../../_utils/file-system-utils";

export class TenantDataFileWrapper {
  constructor(tenantDataFile: TenantDataFile, selected: boolean) {
    this.tenantDataFile = tenantDataFile;
    this.selected = selected;
  }
  tenantDataFile: TenantDataFile;
  selected: boolean;
}

export class FileList {
  file: File;
  fileName: string;
  fullPath: string;
  fileStatus: string;
  locationId: string;

  constructor(
    file: File,
    fileName: string,
    fullPath: string,
    fileStatus: string,
    locationId: string
  ) {
    this.file = file;
    this.fileName = fileName;
    this.fullPath = fullPath;
    this.fileStatus = fileStatus;
    this.locationId = locationId;
  }
}

@Component({
    selector: "app-data-upload-dialog",
    templateUrl: "./data-upload-dialog.component.html",
    styleUrls: ["./data-upload-dialog.component.scss"],
    standalone: false
})
export class DataUploadDialogComponent implements OnDestroy {
  @Input() readonly SelectionMode = SelectionMode;

  readonly saveMode = "Save";
  readonly iplexGMode = "IPLEX_G";
  readonly receiveVideoMimetype = "application/octet-stream";
  readonly mp4Mimetype = "video/mp4";
  readonly jsonMimetype = "application/json";

  browserFlag = true;
  selectFolderFlag = false;
  isDragOver = false;
  isReadCompleted = false;
  inspection: Inspection;
  inspectionName: string;
  inspectionReport: InspectionReport = new InspectionReport();
  fileList: FileList[] = [];
  imageFileList: File[] = [];
  imageFileNameList: string[] = [];
  imageFileResultList: { [filePath: string]: string };
  completeFileCount = 0;
  successFileCount = 0;
  failureFileCount = 0;
  progressDialogRef;
  subscriptions: Array<Subscription> = [];
  isCancel = false;
  // for OSC data linkage
  isDeletedWithUploading = true;
  mode = this.saveMode;
  isMobileDevice = false;
  public acceptedImages: string;

  @Output() uploaded = new EventEmitter<boolean>();

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  get isDeviceConnectMode() {
    return this.isOffline && this.iplexGConnectModeService.isHttpsConnectMode;
  }

  private onCancel$ = new Subject();
  private onDestroy$ = new Subject();

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private dataUploadDialogRef: MatDialogRef<DataUploadDialogComponent>,
    private snackBar: CommonSnackBarService,
    private imageUploadOnlineService: ImageUploadService,
    private imageUploadOfflineService: ImageUploadOfflineService,
    private utilsService: UtilsService,
    private zone: NgZone,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private networkStatusService: NetworkStatusService,
    private iplexGConnectModeService: IplexGConnectModeService,
    private iplexGSyncService: IplexGSyncService,
    private oscSpinnerService: SpinnerService,
    private electronService: ElectronService,
    private fileSysAccessService: FileSystemAccessService,
    private uploadProgressDialogService: UploadProgressDialogService
  ) {
    this.langService.prepare(translate, this.onDestroy$);

    if (!this.data?.inspection?.id) {
      throw new ApplicationError("Inspection is invalid.");
    }

    this.inspection = this.data.inspection;
    this.inspectionName = this.inspection?.title;

    this.isMobileDevice = this.networkStatusService.isMobileDevice();

    this.evaluateAcceptedImages();
  }

  public get useApplicationFileManager() {
    return this.networkStatusService.useApplicationFileManager();
  }

  private evaluateAcceptedImages() {
    const acceptedExtensions = [
      IMAGE_FILETYPE.EXTENSION_PNG,
      IMAGE_FILETYPE.EXTENSION_BMP,
      IMAGE_FILETYPE.EXTENSION_TIF,
      IMAGE_FILETYPE.EXTENSION_TIFF,
      IMAGE_FILETYPE.EXTENSION_HEIC,
      IMAGE_FILETYPE.EXTENSION_HEIF,
      IMAGE_FILETYPE.EXTENSION_JPG,
      IMAGE_FILETYPE.EXTENSION_JPEG,
      IMAGE_FILETYPE.EXTENSION_JFIF,
      IMAGE_FILETYPE.EXTENSION_MP4,
      IMAGE_FILETYPE.EXTENSION_JSON,
      IMAGE_FILETYPE.EXTENSION_JSN,
      IMAGE_FILETYPE.EXTENSION_WAV,
      IMAGE_FILETYPE.EXTENSION_AVI,
    ];

    this.acceptedImages = acceptedExtensions.map((ext) => `.${ext}`).join(",");
  }

  ngOnDestroy(): void {
    this.cancelFileUpload();
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  isChromeOrEdgeBrowser(): boolean {
    const browserType = this.utilsService.getBrowserName();
    return (
      browserType === BROWSER_TYPE.EDGE || browserType === BROWSER_TYPE.CHROME
    );
  }

  processSelectedFiles(files: File[]): boolean {
    if (files.length === 0) {
      return false;
    }

    this.selectFolderFlag = false;
    this.isReadCompleted = false;
    this.imageFileList = [];
    this.imageFileNameList = [];
    this.imageFileResultList = {};

    // Normalize MIME type for video files before processing
    const normalizedFiles = Array.from(files).map(file => {
      if (this.networkStatusService.isDesktop()) {
        let newType = null;

        if (file.type === IMAGE_FILETYPE.AVI_XMSVIDEO || file.type === IMAGE_FILETYPE.AVI_MSVIDEO) {
          newType = IMAGE_FILETYPE.AVI;
        } else if (file.type === IMAGE_FILETYPE.MP4_APPLICATION) {
          newType = IMAGE_FILETYPE.MP4;
        }

        if (newType)
          return this.createFileWithNewType(file, newType);
      }

      return file;
    });

    if (this.isDesktop && this.isOffline) {
      this.imageUploadOfflineService.loadFilesForFolder(
        this.inspection.id,
        normalizedFiles
      );
      this.fileList = this.imageUploadOfflineService.fileList;
      this.imageFileNameList = this.imageUploadOfflineService.imageFileNameList;
      this.imageFileResultList =
        this.imageUploadOfflineService.imageFileResultList;
    } else {
      for (const normalizedFile of normalizedFiles) {
        if (this.isUploadableFile(normalizedFile.type, normalizedFile.size, normalizedFile.name)) {
          // It makes the name of the image from ios Camera unique
          const modifiedFile =
            this.networkStatusService.isIOSDevice() && normalizedFile.name === "image.jpg"
              ? new File([normalizedFile], `image_${normalizedFile.lastModified}.jpg`, {
                  type: normalizedFile.type,
                  lastModified: normalizedFile.lastModified,
                })
              : normalizedFile;

          this.processFile(modifiedFile, normalizedFile.webkitRelativePath);
        }
      }
    }

    this.isReadCompleted = true;
    return true;
  }

  private processFile(file: File, relativePath: string | null) {
    const filePath = this.getUploadPath(relativePath || file.name);

    this.fileList.push(
      new FileList(
        file,
        filePath,
        relativePath,
        UPLOAD_RESULT_CODE.BEFOREUPLOAD,
        null
      )
    );

    this.imageFileList.push(file);
    this.imageFileNameList.push(relativePath || file.name);
    this.imageFileResultList[relativePath || file.name] =
      UPLOAD_RESULT_CODE.BEFOREUPLOAD;
  }

  private getUploadPath(fullPath: string): string {
    return getImageUploadPath(fullPath);
  }

  isUploadableFile(
    filetype: string,
    filesize: number,
    filename: string
  ): boolean {
    return (
      InspectionFileValidator.isValidInspectionFileType(filetype, filename) &&
      filesize > 0
    );
  }

  async uploadFile() {
    if (this.isOffline) {
      if (this.fileList.length !== 0) {
        this.imageUploadOfflineService.uploadSuccessObservable
          .pipe(take(1))
          .subscribe((isSuccess) => this.uploaded.emit(isSuccess));

        this.imageUploadOfflineService.uploadFileList(
          this.fileList,
          this.imageFileNameList,
          null
        );
      }
    } else {
      this.imageUploadOnlineService.uploadCompleted$
        .pipe(take(1))
        .subscribe(() => this.uploaded.emit(true));
      this.imageUploadOnlineService.uploadInspectionFolder(
        this.inspection.id,
        this.fileList
      );
    }
  }

  private createFileWithNewType(file: File, newType: string): File {
    const newFile = new File([file], file.name, {
      type: newType,
      lastModified: file.lastModified,
    });

    // Preserve path properties
    if ("path" in file) {
      Object.defineProperty(newFile, 'path', { value: file.path });
    }

    if (file.webkitRelativePath) {
      Object.defineProperty(newFile, 'webkitRelativePath', { value: file.webkitRelativePath });
    }

    return newFile;
  }

  cancelFileUpload(): boolean {
    this.isCancel = true;
    this.completeFileCount = 0;
    this.oscSpinnerService.hide(true);
    this.onCancel$.next(undefined);
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });

    if (this.isOffline) {
      this.iplexGSyncService.cancelFileTransfer();
      this.fileSysAccessService.deleteFolder(FILE_SYSTEM.PATH.TEMP);
    }

    return true;
  }

  openUploadProgressDialog(fileCount: number = null): boolean {
    this.uploadProgressDialogService.init();

    if (fileCount) {
      this.uploadProgressDialogService.totalFileCount = fileCount;
    } else {
      this.uploadProgressDialogService.totalFileCount = this.fileList.length;
    }

    this.progressDialogRef = this.dialog.open(UploadProgressDialogComponent, {
      width: UploadProgressDialogComponent.defaultDialogWidth,
      maxHeight: UploadProgressDialogComponent.defaultDialogMaxHeight,
      minHeight: UploadProgressDialogComponent.defaultDialogMinHeight,
      disableClose: true,
    });
    this.imageUploadOnlineService.progressDialogRef = this.progressDialogRef;

    this.progressDialogRef.afterClosed().subscribe((result) => {
      this.cancelFileUpload();
    });
    this.progressDialogRef.componentInstance.dialogClose.subscribe((data) => {
      if (!this.isErrorExists()) {
        this.progressDialogRef.close(true);
      } else {
        this.isCancel = true;
        this.uploadProgressDialogService.isError = true;
        this.uploadProgressDialogService.imageFileResultList = JSON.parse(
          JSON.stringify(this.imageFileResultList)
        );
        this.subscriptions.forEach((subscription: Subscription) => {
          subscription.unsubscribe();
        });
      }
    });
    return true;
  }

  openReceiveProgressDialog(
    totalFileCount: number,
    totalFileSize: number,
    startTime: number
  ): boolean {
    this.progressDialogRef =
      this.imageUploadOfflineService.openReceiveProgressDialog(
        totalFileCount,
        totalFileSize,
        startTime
      );

    this.progressDialogRef.afterClosed().subscribe((result) => {
      this.cancelFileUpload();
    });

    this.progressDialogRef.componentInstance.dialogClose.subscribe((data) => {
      this.oscSpinnerService.hide(true);
      if (!this.isErrorExists()) {
        this.progressDialogRef.close(true);
      } else {
        this.isCancel = true;
        this.uploadProgressDialogService.isError = true;
        this.uploadProgressDialogService.successFileCount =
          this.successFileCount;
        this.uploadProgressDialogService.imageFileResultList = JSON.parse(
          JSON.stringify(this.imageFileResultList)
        );
        this.subscriptions.forEach((subscription: Subscription) => {
          subscription.unsubscribe();
        });
      }
    });
    return true;
  }

  isErrorExists(): boolean {
    let isError = false;
    for (const result in this.imageFileResultList) {
      if (this.imageFileResultList[result] === UPLOAD_RESULT_CODE.FAILED) {
        isError = true;
      }
    }
    return isError;
  }

  onDragOver(event: DragEvent) {
    this.isDragOver = true;
    event.dataTransfer.dropEffect = "copy";
    event.stopPropagation();
    event.preventDefault();
  }

  onDragLeave(event: DragEvent) {
    this.isDragOver = false;
  }

  onDropZip(event: DragEvent) {
    this.selectFolderFlag = true;
    this.isDragOver = false;
    event.stopPropagation();
    event.preventDefault();
    this.imageFileList = [];
    this.fileList = [];
    this.imageFileNameList = [];
    this.imageFileResultList = {};
    if (event.dataTransfer.files[0]) {
      this.fileList.push(
        new FileList(
          event.dataTransfer.files[0],
          event.dataTransfer.files[0].name,
          event.dataTransfer.files[0].name,
          UPLOAD_RESULT_CODE.BEFOREUPLOAD,
          null
        )
      );
      this.imageFileList.push(event.dataTransfer.files[0]);
      this.imageFileNameList.push(event.dataTransfer.files[0].name);
    }
  }

  onDropFolder(event: DragEvent) {
    this.selectFolderFlag = true;
    this.isReadCompleted = false;
    this.isDragOver = false;
    event.stopPropagation();
    event.preventDefault();
    this.imageFileList = [];
    this.imageFileNameList = [];
    this.imageFileResultList = {};
    this.fileList = [];
    if (this.isOffline) {
      this.imageUploadOfflineService
        .loadFiles(this.inspection.id, event.dataTransfer.items)
        .then(() => {
          this.fileList = this.imageUploadOfflineService.fileList;
          // this.imageFileList = this.imageUploadOfflineService.imageFileList;
          this.imageFileNameList =
            this.imageUploadOfflineService.imageFileNameList;
          this.imageFileResultList =
            this.imageUploadOfflineService.imageFileResultList;
          this.isReadCompleted = true;
        });
    } else {
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        if (event.dataTransfer.items[i]) {
          const entry = event.dataTransfer.items[i].webkitGetAsEntry();
          const obs = [];

          if (entry.isFile) {
            obs.push(
              new Observable<any>((observer) =>
                this.traverseEntry(entry, observer)
              )
            );
            forkJoin(obs).subscribe((e) => {
              this.zone.run(() => {
                this.isReadCompleted = true;
              });
            });
          } else {
            const dirEntry = entry as FileSystemDirectoryEntry;
            const reader = dirEntry.createReader();
            obs.push(
              new Observable<any>((observer) =>
                this.folderRead(reader, observer)
              )
            );
            forkJoin(obs).subscribe((e) => {
              this.zone.run(() => {
                this.isReadCompleted = true;
              });
            });
          }
        }
      }
    }
  }

  folderRead(reader, observer: Subscriber<void>) {
    reader.readEntries((entries) => {
      const obs = [];
      for (let i = 0, len = entries.length; i < len; i++) {
        obs.push(
          new Observable<any>((innerObserver) =>
            this.traverseEntry(entries[i], innerObserver)
          )
        );
      }
      if (entries.length > 0) {
        obs.push(
          new Observable<any>((innerObserver) =>
            this.folderRead(reader, innerObserver)
          )
        );
        forkJoin(obs).subscribe((e) => {
          observer.next();
          observer.complete();
        });
      } else {
        observer.next();
        observer.complete();
      }
    });
  }

  traverseEntry(entry, observer: Subscriber<void>) {
    switch (true) {
      // ファイルの場合
      case entry.isFile:
        entry.file((file) => {
          this.zone.run(() => {
            if (this.isUploadableFile(file.type, file.size, file.name)) {
              const filePath = this.getUploadPath(entry.fullPath);
              this.imageFileList.push(file);
              this.imageFileNameList.push(entry.fullPath);
              this.imageFileResultList[entry.fullPath] =
                UPLOAD_RESULT_CODE.BEFOREUPLOAD;
              this.fileList.push(
                new FileList(
                  file,
                  filePath,
                  entry.fullPath,
                  UPLOAD_RESULT_CODE.BEFOREUPLOAD,
                  null
                )
              );
            }
            observer.next();
            observer.complete();
          });
        });
        break;
      // ディレクトリの場合
      case entry.isDirectory:
        const dirEntry = entry as FileSystemDirectoryEntry;
        const reader = dirEntry.createReader();
        const obs = [];

        obs.push(
          new Observable<any>((observer) => this.folderRead(reader, observer))
        );
        forkJoin(obs).subscribe((e) => {
          observer.next();
          observer.complete();
        });
    }
  }

  transferImageFromDevice(importIplexGImageWrapper: ImportIplexGImageWrapper) {
    if (!this.isDesktop || !this.isOffline) {
      return;
    }

    // common
    this.isCancel = false;
    this.completeFileCount = 0;
    this.failureFileCount = 0;
    this.successFileCount = 0;
    this.imageUploadOfflineService.uploadInit(this.inspection.id);
    this.imageUploadOfflineService.uploadSuccessObservable
      .pipe(take(1), takeUntil(this.onDestroy$), takeUntil(this.onCancel$))
      .subscribe((isSuccess) => this.uploaded.emit(isSuccess));

    this.electronService.onEvent(
      IPC_CHANNEL.WEB_DAV.STREAM_SIZE,
      (_event, reply) => {
        this.zone.run(() => {
          this.uploadProgressDialogService.countLoadedFileSize(reply);
        });
      }
    );

    const imagesWrappers = importIplexGImageWrapper.imageFiles;
    const getDeviceFilePms = imagesWrappers.map(async (wrapper) => {
      const contentPath = wrapper.fileStat.filename;
      const tempFilePath = FILE_SYSTEM.PATH.TEMP + contentPath;
      const fileName = wrapper.fileStat.basename;
      const mimeType =
        this.receiveVideoMimetype === wrapper.fileStat.mime
          ? this.mp4Mimetype
          : wrapper.fileStat.mime;

      const $saved = new Subject<boolean>();
      const eventChannel = IPC_CHANNEL.WEB_DAV.SAVE_STREAM + contentPath;
      this.electronService.onEvent(eventChannel, (_event, reply) =>
        $saved.next(true)
      );

      const mainFilePm = this.iplexGSyncService
        .getFileFromSDcardStream(contentPath)
        .pipe(switchMapTo($saved.asObservable().pipe(take(1))))
        .toPromise();

      let saveBmFileFilePm = of(null as boolean).toPromise();
      if (mimeType === this.mp4Mimetype && wrapper?.bookMarkStat) {
        saveBmFileFilePm = this.iplexGSyncService
          .getFileFromSDcardStream(wrapper.bookMarkStat.filename, null, false)
          .toPromise();
      }

      let saveAudioFileFilePm = of(null as boolean).toPromise();
      if (wrapper?.audioStat) {
        saveAudioFileFilePm = this.iplexGSyncService
          .getFileFromSDcardStream(wrapper.audioStat.filename, null, false)
          .toPromise();
      }

      return Promise.all([
        mainFilePm,
        saveBmFileFilePm,
        saveAudioFileFilePm,
        this.fileSysAccessService.getSaveBlobFolderPath(),
      ])
        .then((values) => {
          const isSuccess = values[0];
          const blobsPath = values[3];
          this.electronService.removeAllListeners(eventChannel);
          if (!isSuccess) {
            throw new Error("Failed to retrieve file from SD card.");
          }

          let mainFile: File;
          const getMainFilePm = this.fileSysAccessService
            .getFile(tempFilePath, fileName, mimeType)
            .then((file) => {
              mainFile = file;
              return this.fileSysAccessService.deleteFileForce(tempFilePath);
            })
            .then((_) => mainFile);
          let getBmFilePm = of(null as BookMarkInfoWrapper).toPromise();
          let getAudioFilePm = of(null as AudioWrapper).toPromise();

          if (
            mimeType === this.mp4Mimetype &&
            wrapper.bookMarkStat &&
            typeof wrapper.bookMarkStat !== "undefined"
          ) {
            let bookMarkInfoWrapper: BookMarkInfoWrapper;
            const filePath =
              blobsPath + FILE_SYSTEM.PATH.TEMP + wrapper.bookMarkStat.filename;
            getBmFilePm = this.http
              .get<BookMarkInfo>(filePath)
              .toPromise()
              .then((info) => {
                bookMarkInfoWrapper = new BookMarkInfoWrapper(
                  info,
                  wrapper.bookMarkStat.filename
                );
                return this.fileSysAccessService.deleteFileForce(tempFilePath);
              })
              .then((_) => bookMarkInfoWrapper);
          }

          if (wrapper.audioStat && typeof wrapper.audioStat !== "undefined") {
            let audioWrapper: AudioWrapper;
            getAudioFilePm = this.fileSysAccessService
              .getFile(
                FILE_SYSTEM.PATH.TEMP + wrapper.audioStat.filename,
                wrapper.audioStat.basename,
                wrapper.audioStat.mime
              )
              .then((file) => {
                audioWrapper = new AudioWrapper(
                  file,
                  wrapper.audioStat.filename
                );
                return this.fileSysAccessService.deleteFileForce(tempFilePath);
              })
              .then((_) => audioWrapper);
          }

          return Promise.all([getMainFilePm, getBmFilePm, getAudioFilePm]);
        })
        .then((files) => {
          const mainFile = files[0];
          const bookMarkInfoWrapper = files[1];
          const audioWrapper = files[2];

          const deviceFile = new DeviceFile(
            wrapper.fileStat.filename,
            mainFile,
            wrapper.fileStat
          );
          return this.imageUploadOfflineService
            .uploadProcessFromDevice(
              deviceFile,
              this.inspection.id,
              importIplexGImageWrapper.isDeletedWithUploading,
              bookMarkInfoWrapper,
              audioWrapper
            )
            .pipe(takeUntil(this.onCancel$))
            .toPromise();
        });
    });

    const startTime = performance.now();
    this.utilsService
      .chainToPromises(getDeviceFilePms, 12)
      .then((_) => {
        this.fileSysAccessService.deleteFolder(FILE_SYSTEM.PATH.TEMP);
        this.electronService.removeAllListeners(
          IPC_CHANNEL.WEB_DAV.STREAM_SIZE
        );
        this.oscSpinnerService.hide(false);
      })
      .catch((error) => {
        this.oscSpinnerService.hide(false);
        this.snackBar.open(
          this.translate.instant("dataUploadDialog.errorReceiveFromIPLEX")
        );
        this.electronService.removeAllListeners(
          IPC_CHANNEL.WEB_DAV.STREAM_SIZE
        );

        console.log(error);
        this.fileSysAccessService.deleteFolder(FILE_SYSTEM.PATH.TEMP);
        throw new Error("failed import from IPLEX");
      });

    let totalFileSize = 0;
    imagesWrappers.forEach((image) => (totalFileSize += image.fileStat.size));
    this.openReceiveProgressDialog(
      imagesWrappers.length,
      totalFileSize,
      startTime
    );
  }

  changeMode(mode: string) {
    if (this.mode === mode) {
      return;
    }

    this.mode = mode;
    if (this.mode === this.iplexGMode) {
      this.oscSpinnerService.show(true);
    }
  }

  dialogClose() {
    if (this.progressDialogRef) {
      this.progressDialogRef.close(true);
    }
    this.dataUploadDialogRef.close();
  }

  showFileManager({ selectionMode }: Pick<FileManagerConfig, "selectionMode">) {
    const fileManagerConfig: MatDialogConfig<FileManagerConfig> = {
       disableClose: true,
      ...FileManagerComponent.DEFAULT_WINDOW_SIZE,
      data: {
        operationMode: OperationMode.Import,
        selectionMode,
        extensions: IMAGE_FILETYPE_MAP,
        handleImportedSelection: this.processSelectedFiles.bind(this),
      }
    };

    this.dialog.open(FileManagerComponent, fileManagerConfig);
  }
}
